import { useContext } from 'react'
import AnalyticsContext from '../context/AnalyticsContext'
import { Analytics } from '../lib/Analytics'

const MISSING_PROVIDER_MESSAGE =
  'useAnalytics() was called outside an AnalyticsContext provider. Render the component inside <Page>, which provides the Analytics instance.'

// Stands in for renders without a provider (unit tests, server rendering): same type as Analytics, but events go nowhere.
class NoopAnalytics extends Analytics {
  constructor() {
    super({ gtmId: 'noop' })
  }

  event(): void {
    // Intentionally empty: without a provider there is no dataLayer to write to.
  }
}

const noopAnalytics = new NoopAnalytics()

// Evaluated per call, not at module load, so the check follows the current environment.
const isDevelopmentBrowser = (): boolean => process.env.NODE_ENV === 'development' && typeof window !== 'undefined'

/**
 * Returns the Analytics instance supplied by `<Page>`. Never returns null, so callers need no null checks.
 *
 * Without a provider it throws in the development browser, to surface a mis-mounted component early. Everywhere
 * else (production, unit tests, server rendering and `next build`) it returns a no-op instance, so analytics can
 * never break a page.
 */
export const useAnalytics = (): Analytics => {
  const analytics = useContext(AnalyticsContext)

  if (analytics) {
    return analytics
  }
  if (isDevelopmentBrowser()) {
    throw new Error(MISSING_PROVIDER_MESSAGE)
  }
  return noopAnalytics
}
